import {
  Button,
  Box,
  Autocomplete,
  Checkbox,
  CircularProgress,
  Container,
  Divider,
  FormControl,
  FormControlLabel,
  FormHelperText,
  Grid,
  Icon,
  IconButton,
  InputAdornment,
  InputLabel,
  ListItemText,
  MenuItem,
  OutlinedInput,
  Stack,
  Select,
  SvgIcon,
  TextField,
  Typography, RadioGroup, Radio
} from '@mui/material';
import React, { useState, useEffect, useRef } from 'react';
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank';
import CheckBoxIcon from '@mui/icons-material/CheckBox';
import { useForm, register, watch, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import FormProvider from 'hooks/form/form-provider';
import { useSnackbar } from 'components/snackbar';
import { CandidateJobPreferencesRule } from 'models/CandidateProfileSchema';
import TypeAhead from 'components/TypeAhead';
import { endpoints } from 'utils/axios';
import { addJobPreferance, getJobPreferanceDetails, addSingleJobPreference } from 'api/users';
import { useNavigate } from 'react-router-dom';
import { SplashScreen } from 'components/loading-screen';
import { useAuth } from 'hooks/useAuthContext';
import { styled } from '@mui/material/styles';
import CurrencyRupeeIcon from '@mui/icons-material/CurrencyRupee';

const BpIcon = styled('span')(({ theme }) => ({
  borderRadius: 6,
  width: 20,
  height: 20,
  boxShadow: 'inset 0 0 0 1.5px rgba(16,22,26,.2), inset 0 -1px 0 rgba(16,22,26,.1)',
  backgroundImage: 'linear-gradient(180deg,hsla(0,0%,100%,.8),hsla(0,0%,100%,0))',
  '.Mui-focusVisible &': {
    outline: '2px auto rgba(19,124,189,.6)',
    outlineOffset: 2,
  },
  'input:hover ~ &': {
    backgroundColor: '#ebf1f5',
    ...theme.applyStyles('dark', {
      backgroundColor: '#30404d',
    }),
  },
  'input:disabled ~ &': {
    boxShadow: 'none',
    background: 'rgba(206,217,224,.5)',
    ...theme.applyStyles('dark', {
      background: 'rgba(57,75,89,.5)',
    }),
  },
  ...theme.applyStyles('dark', {
    boxShadow: '0 0 0 1px rgb(16 22 26 / 40%)',
    backgroundColor: '#394b59',
    backgroundImage: 'linear-gradient(180deg,hsla(0,0%,100%,.05),hsla(0,0%,100%,0))',
  }),
}));

const BpCheckedIcon = styled(BpIcon)({
  backgroundColor: '#137cbd',
  backgroundImage: 'linear-gradient(180deg,hsla(0,0%,100%,.1),hsla(0,0%,100%,0))',
  '&::before': {
    display: 'block',
    width: 20,
    height: 20,
    backgroundImage:
      "url(\"data:image/svg+xml;charset=utf-8,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 16 16'%3E%3Cpath" +
      " fill-rule='evenodd' clip-rule='evenodd' d='M12 5c-.28 0-.53.11-.71.29L7 9.59l-2.29-2.3a1.003 " +
      "1.003 0 00-1.42 1.42l3 3c.18.18.43.29.71.29s.53-.11.71-.29l5-5A1.003 1.003 0 0012 5z' fill='%23fff'/%3E%3C/svg%3E\")",
    content: '""',
  },
  'input:hover ~ &': {
    backgroundColor: '#106ba3',
  },
});

/**
 * A custom checkbox component that renders a checkbox with a blue checkmark icon on the inside.
 * It uses the `BpIcon` and `BpCheckedIcon` components to render the icon and the checkmark respectively.
 * The hover style is overriden to prevent the checkbox from changing background color on hover.
 * @param {{}} props - The props of the component.
 * @returns {ReactElement} The custom checkbox component.
 */
function BpCheckbox(props) {
  return (
    <Checkbox
      sx={{ '&:hover': { bgcolor: 'transparent' } }}
      disableRipple
      color="default"
      checkedIcon={<BpCheckedIcon />}
      icon={<BpIcon />}
      inputProps={{ 'aria-label': 'Checkbox demo' }}
      {...props}
    />
  );
}

const JobPreferences = () => {
  const { enqueueSnackbar } = useSnackbar();
  const { user, updateContext, setJobPreferenceData, jobPreferenceData, setShowJobStatusDialog } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [isUpdatingNegotiable, setIsUpdatingNegotiable] = useState(false);
  const [onSelect, setOnSelect] = useState(0);
  const [jobPreference, setJobPreference] = useState(['both']);
  const [isChange, setIsChange] = useState(false);

  const [inputValue, setInputValue] = useState('');

  const buyoutFieldRef = useRef(null);
  // const topRef = useRef(null);

  const workModelFormat = ['In office', 'Work from Home', 'Hybrid'];
  const preferredJobShifts = ['Daytime', 'Evening', 'Night'];
  const jobKind = [
    { label: "Either (Full Time as well as Part Time)", value: "both" },
    { label: "Full Time", value: "Full time" },
    { label: "Part Time/Temporary/Internship", value: "Part time" },
  ];

  const defaultValues = {
    notice_period: '',
    buyout_period: '',
    is_negotiable: false,
    total_ctc: null,
    compensation: '',
    fixed_ctc: null,
    variable_ctc: null,
    other_ctc: '',
    expected_ctc: '',
    work_format: ['In office'],
    job_type: ['both'],
    preferred_job_shifts: ['Daytime'],
    prefered_location: [],
    hour: null,
    hourly_rate: null
  };

  const methods = useForm({
    defaultValues,
    mode: 'onChange',
    resolver: yupResolver(CandidateJobPreferencesRule(0, jobPreference)),
  });

  const {
    control,
    register,
    handleSubmit,
    setValue,
    watch,
    getValues,
    setError,
    clearErrors,
    formState: { touchedFields, errors, isDirty, isValid, dirtyFields, isSubmitting, submitCount },
  } = methods;

  useEffect(() => {
    /**
     * Fetches the job preferences for the user and updates the form state accordingly.
     * @returns {Promise<void>}
     */
    const fetchjobs = async () => {
      try {
        const response = await getJobPreferanceDetails();
        if (response?.success && response.data) {
          setJobPreferenceData(response?.data);
          Object.keys(defaultValues).forEach((field) => {
            setValue(field, response.data[field]);
          });
          setOnSelect(response?.data?.prefered_location?.length);
          setJobPreference(response?.data?.job_type || ['both']);
        }
      } catch (error) {
        console.error('Error fetching jobs:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchjobs();
  }, []);

  useEffect(() => {
    const subscription = watch((value) => {
      const preferredLocations = value?.prefered_location;
      if (Array.isArray(preferredLocations)) {
        if (preferredLocations.length > 5) {
          const limitedLocations = preferredLocations.slice(0, 5);
          setValue('prefered_location', limitedLocations);
        }
      }
    });
    return () => subscription.unsubscribe();
  }, [watch]);

  useEffect(() => {
    const fixedRaw = getValues('fixed_ctc')?.toString().replace(/,/g, '') || '0';
    const variableRaw = getValues('variable_ctc')?.toString().replace(/,/g, '') || '0';

    const total = parseInt(fixedRaw, 10) + parseInt(variableRaw, 10);
    setValue('total_ctc', total);
  }, [getValues('fixed_ctc'), getValues('variable_ctc')]);

  useEffect(() => {
    if (onSelect > 5) setTimeout(() => setOnSelect(5), 3000)
  }, [onSelect]);

  useEffect(() => {
    if (jobPreferenceData?.validation === false) {
      onSubmit();
    }
  }, [jobPreferenceData]);

  // Scroll to first error field on validation failure
  useEffect(() => {
    if (Object.keys(errors).length > 0) {
      const firstErrorKey = Object.keys(errors)[0];
      console.log('First error field:', firstErrorKey);

      // Try to find the error field element
      const errorElement =
        document.querySelector(`[name="${firstErrorKey}"]`) ||
        document.querySelector(`#${firstErrorKey}`) ||
        document.querySelector(`[id="${firstErrorKey}"]`);

      if (errorElement) {
        console.log('Scrolling to error field:', firstErrorKey);
        errorElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
      } else {
        console.log('Element not found, scrolling to top');
        // topRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }
  }, [errors]);

  const onSubmit = handleSubmit(async (formData) => {
    const response = await addJobPreferance(formData);
    if (response?.success === true && response?.data) {
      const wasAtStageThree = user?.stages === 3;
      updateContext({ stages: 4 });

      const jobID = localStorage.getItem('jobIdForNewUser');
      if (jobID) {
        navigate(`/jobs/${jobID}`);
        localStorage.removeItem('jobIdForNewUser');
      } else {
        if (wasAtStageThree) {//first time user
          navigate('/home', {
            replace: false,
            state: { dialogIds: ['jobStatusDialog'] },
          });
        } else navigate('/home');
      }

      if (isChange) enqueueSnackbar(response.message, { variant: 'success' });
    } else if (response?.success === false) {
      enqueueSnackbar(response.message, { variant: 'error' });
    }
  });

  /**
   * Handles the change of a single-select or multi-select field.
   * Updates the state with the new value and calls handleSingleChange with the new value.
   * If the new value includes 'Any', it sets the value of the field to all values.
   * If the previous value included 'Any' and the new value does not, it sets the value of the field to an empty array.
   * Otherwise, it simply sets the value of the field to the new value.
   * @param {string} name - The name of the field.
   * @param {array} previousValue - The previous value of the field.
   * @param {array} value - The new value of the field.
   * @param {array} allValue - All the values of the field.
   */
  const handleChange = (name, previousValue, value, allValue) => {
    if (value?.includes('Any') && !previousValue?.includes('Any')) {
      setValue(name, allValue);
      handleSingleChange({ [name]: allValue });
    } else if (previousValue?.includes('Any') && !value?.includes('Any')) {
      setValue(name, []);
      handleSingleChange({ [name]: [] });
    } else {
      setValue(name, value);
      handleSingleChange({ [name]: value });
    }
  };

  /**
   * Handles the change of a single field in the job preferences.
   * Calls the addSingleJobPreference API with the updated field data.
   * If the API call is successful, it updates the job preference data and sets isChange to true.
   * If the API call fails, it displays an error message.
   * @param {object} data - The updated field data.
   */
  const handleSingleChange = async (data) => {
    const response = await addSingleJobPreference(data);
    if (response?.success === true && response?.data) {
      setIsChange(true);
      setJobPreferenceData(response?.data?.job_preferences);
    } else if (response?.success === false) {
      enqueueSnackbar(response.message, { variant: 'error' });
    }
  };

  /**
   * Handles the change of the job type preference.
   * Calls the addSingleJobPreference API with the updated job type data.
   * If the API call is successful, it updates the job preference data and sets isChange to true.
   * If the API call fails, it displays an error message.
   * @param {string} selected - The selected job type.
   */
  const handleJobType = (selected) => {
    handleSingleChange({ job_type: [selected] });
    setJobPreference([selected]);
    setJobPreferenceData({ ...jobPreferenceData, job_type: [selected] });
  };

  if (loading) {
    return (
      <Grid container justifyContent="center" alignItems="center">
        <SplashScreen />
      </Grid>
    );
  };

  return (
    <Box className="page-content">
      <FormProvider methods={methods} onSubmit={onSubmit}>
        <Box className="job-preferences-page">
          <Container maxWidth="xl">
            <Box className="job-preferences-content-wrapper">
              <Box className="current-job-det" mb={2.5}>
                <Stack spacing={0.5}>
                  <Typography className="main-heading" variant="h6" >Update Job Details & Preferences</Typography>
                  <Typography className="step-info" variant="body2">These details would help us match the most suitable jobs for your profile</Typography>
                </Stack>
              </Box>

              <Box mb={2.5}>
                <Box className="card-container">
                  <Box className="card-title">
                    <Typography className="title-text">General Job Preferences</Typography>
                  </Box>
                  <Box className="card-body">
                    <Typography className="step-heading" variant="h6" sx={{ marginBottom: '16px!important' }}>What kind of job you are looking for?</Typography>
                    <Box mb={3} mt={2}>
                      {/* job type */}
                      <FormControl sx={{ width: '100%' }}>
                        <Controller
                          control={control}
                          name="job_type"
                          render={({ field }) => (
                            <RadioGroup
                              row
                              value={field.value?.[0] || ''}
                              onChange={(e) => {
                                const selected = e.target.value;
                                field.onChange([selected]);
                                handleJobType(selected);
                              }}
                            >
                              {jobKind?.map((job) => (
                                <FormControlLabel
                                  key={job?.value}
                                  value={job?.value}
                                  control={<Radio />}
                                  label={job?.label}
                                />
                              ))}
                            </RadioGroup>
                          )}
                        />
                      </FormControl>
                    </Box>
                    <Box>
                      <Grid container spacing={{ xs: 2.5, md: 5 }}>
                        <Grid item xs={12} md={6}>
                          {/* work format */}
                          <FormControl fullWidth size="small" sx={{ marginBottom: '24px' }}>
                            <Controller
                              control={control}
                              name="work_format"
                              id="work_format"
                              render={({ field }) => (
                                <Autocomplete
                                  {...field}
                                  multiple
                                  size="small"
                                  id="work_format"
                                  options={workModelFormat}
                                  getOptionLabel={(option) => option}
                                  disableCloseOnSelect={true}
                                  onChange={(event, value) => {
                                    field.onChange(value);
                                    handleSingleChange({ work_format: value });
                                  }}
                                  renderOption={(props, option, { selected }) => (
                                    <li {...props}>
                                      <Checkbox
                                        icon={<CheckBoxOutlineBlankIcon fontSize="small" />}
                                        checkedIcon={<CheckBoxIcon fontSize="small" />}
                                        checked={selected}
                                      />
                                      {option}
                                    </li>
                                  )}
                                  renderInput={(params) => (
                                    <TextField
                                      {...params}
                                      variant="outlined"
                                      label="Preferred Work Format"
                                      error={!!errors.work_format}
                                      helperText={errors.work_format?.message}
                                    />
                                  )}
                                />
                              )}
                            />
                            <FormHelperText>Select one or more options</FormHelperText>
                          </FormControl>
                          {/* location */}
                          <FormControl fullWidth size="small">
                            <Controller
                              control={control}
                              name="prefered_location"
                              defaultValue={defaultValues?.prefered_location || []}
                              render={({ field }) => (
                                <TypeAhead
                                  {...field}
                                  {...register('prefered_location')}
                                  label="Preferred Locations (select upto 5)"
                                  multiple
                                  selectAsCheckbox
                                  isCloseHide
                                  disableCloseOnSelect
                                  api={`${process.env.REACT_APP_END_POINT}${endpoints?.services?.cluster}`}
                                  size="small"
                                  displayTextKey="name"
                                  maxSelection={5}
                                  inputValue={inputValue}
                                  onInputChange={(e, newInputValue, reason) => {
                                    setInputValue(newInputValue);
                                  }}
                                  onBlur={() => {
                                    setInputValue('');
                                  }}
                                  onChange={(e, dataObjs, values) => {
                                    if (values?.length <= 5) {
                                      setOnSelect(values?.length);
                                      values?.length > 0 && handleSingleChange({ prefered_location: values });
                                      setInputValue('');
                                    } else {
                                      setOnSelect(onSelect + 1);
                                    }
                                  }}
                                />
                              )}
                            />
                            {(onSelect && onSelect > 5) ? <FormHelperText error>You can select up to 5 locations only</FormHelperText> : null}
                            <FormHelperText>Select one or more options</FormHelperText>
                          </FormControl>
                        </Grid>
                        <Grid item xs={12} md={6}>
                          {/* job shift(working hours) */}
                          <FormControl fullWidth>
                            <Controller
                              control={control}
                              name="preferred_job_shifts"
                              render={({ field }) => (
                                <Autocomplete
                                  {...field}
                                  multiple
                                  size="small"
                                  options={preferredJobShifts}
                                  getOptionLabel={(option) => option}
                                  disableCloseOnSelect={true}
                                  onChange={(event, value) => {
                                    handleChange(field?.name, field?.value, value, preferredJobShifts);
                                  }}
                                  renderOption={(props, option, { selected }) => (
                                    <li {...props}>
                                      <Checkbox
                                        icon={<CheckBoxOutlineBlankIcon fontSize="small" />}
                                        checkedIcon={<CheckBoxIcon fontSize="small" />}
                                        checked={selected}
                                      />
                                      {option}
                                    </li>
                                  )}
                                  renderInput={(params) => (
                                    <TextField
                                      {...params}
                                      label="Preferred Working Hours"
                                      variant="outlined"
                                      error={!!errors.preferred_job_shifts}
                                      helperText={errors.preferred_job_shifts?.message}
                                    />
                                  )}
                                />
                              )}
                            /><FormHelperText>Select one or more options</FormHelperText>
                          </FormControl>
                        </Grid>
                      </Grid>
                    </Box>
                  </Box>
                </Box>
              </Box>
              {/* full time */}
              {(jobPreference?.includes('Full time') || jobPreference?.includes('both')) &&
                <Box mb={2.5}>
                  <Box className="card-container">
                    <Box className="card-title">
                      <Typography className="title-text">Full Time Job Details</Typography>
                    </Box>
                    <Box className="card-body">
                      <Typography className="step-heading" variant="h6" sx={{ marginBottom: '16px!important' }}>Current Annual Compensation</Typography>
                      <Box className="annual-compensation" mb={3} mt={2}>
                        <Box>
                          <Grid container spacing={{ xs: 2.5, md: 5 }}>
                            <Grid item xs={12} md={6}>
                              <Grid container spacing={2} mb={2}>
                                <Grid item xs={12} md={6}>
                                  {/* fixed ctc */}
                                  <FormControl fullWidth error={!!errors?.fixed_ctc}>
                                    <Controller
                                      control={control}
                                      name="fixed_ctc"
                                      defaultValue={defaultValues?.fixed_ctc || ''}
                                      render={({ field }) => {
                                        const [isEditing, setIsEditing] = React.useState(false);

                                        return (
                                          <TextField
                                            {...field}
                                            id="fixed_ctc"
                                            name="fixed_ctc"
                                            label="Fixed Component"
                                            variant="outlined"
                                            size="small"
                                            error={!!errors.fixed_ctc}
                                            type="text"
                                            value={
                                              isEditing
                                                ? field.value
                                                : field.value
                                                  ? new Intl.NumberFormat('en-IN').format(field.value)
                                                  : ''
                                            }
                                            onFocus={() => setIsEditing(true)}
                                            onBlur={(e) => {
                                              setIsEditing(false);

                                              let rawValue = e.target.value.replace(/,/g, '');
                                              let numValue = parseInt(rawValue, 10);

                                              if (!isNaN(numValue)) {
                                                field.onChange(numValue);
                                                const variableValue = parseInt(getValues('variable_ctc'), 10) || 0;
                                                const total = numValue + variableValue;
                                                setValue('total_ctc', total);
                                                handleSingleChange({ fixed_ctc: numValue, total_ctc: total });
                                              }
                                            }}
                                            onChange={(e) => {
                                              let rawValue = e.target.value.replace(/,/g, '');
                                              if (/^\d{0,9}$/.test(rawValue)) {
                                                field.onChange(rawValue);
                                              }
                                            }}
                                            inputProps={{
                                              inputMode: 'numeric',
                                              pattern: '[0-9]*',
                                              maxLength: 9
                                            }}
                                            InputProps={{
                                              startAdornment: (
                                                field?.value ? (
                                                  <InputAdornment position="start">
                                                    <CurrencyRupeeIcon fontSize="small" />
                                                  </InputAdornment>) : null
                                              ),
                                            }}
                                          />
                                        );
                                      }}
                                    />
                                    {errors?.fixed_ctc && <FormHelperText error>{errors.fixed_ctc.message}</FormHelperText>}
                                  </FormControl>
                                </Grid>
                                <Grid item xs={12} md={6}>
                                  {/* variable ctc */}
                                  <FormControl fullWidth error={!!errors?.variable_ctc}>
                                    <Controller
                                      control={control}
                                      name="variable_ctc"
                                      defaultValue={defaultValues?.variable_ctc || ''}
                                      render={({ field }) => {
                                        const [isEditing, setIsEditing] = React.useState(false);

                                        return (
                                          <TextField
                                            {...field}
                                            id="variable_ctc"
                                            name="variable_ctc"
                                            label="Variable Component"
                                            variant="outlined"
                                            size="small"
                                            error={!!errors.variable_ctc}
                                            helperText={errors.variable_ctc?.message}
                                            type="text"
                                            value={
                                              isEditing
                                                ? field.value
                                                : field.value
                                                  ? new Intl.NumberFormat('en-IN').format(field.value)
                                                  : ''
                                            }
                                            onFocus={() => setIsEditing(true)}
                                            onBlur={(e) => {
                                              setIsEditing(false);

                                              let rawValue = e.target.value.replace(/,/g, '');
                                              let numValue = parseInt(rawValue, 10);

                                              if (!isNaN(numValue)) {
                                                field.onChange(numValue);
                                                const fixedValue = parseInt(getValues('fixed_ctc'), 10) || 0;
                                                const total = numValue + fixedValue;
                                                setValue('total_ctc', total);
                                                handleSingleChange({ variable_ctc: numValue, total_ctc: total });
                                              }
                                            }}
                                            onChange={(e) => {
                                              let rawValue = e.target.value.replace(/,/g, '');
                                              if (/^\d{0,9}$/.test(rawValue)) {
                                                field.onChange(rawValue);
                                              }
                                            }}
                                            inputProps={{
                                              inputMode: 'numeric',
                                              pattern: '[0-9]*',
                                              maxLength: 9
                                            }}
                                            InputProps={{
                                              startAdornment: (
                                                field?.value ? (
                                                  <InputAdornment position="start">
                                                    <CurrencyRupeeIcon fontSize="small" />
                                                  </InputAdornment>) : null
                                              ),
                                            }}
                                          />
                                        );
                                      }}
                                    />
                                    {/* {errors?.variable_ctc && <FormHelperText error>{errors.variable_ctc.message}</FormHelperText>} */}
                                  </FormControl>
                                </Grid>
                              </Grid>
                              {/* other ctc */}
                              <FormControl fullWidth>
                                <Controller
                                  control={control}
                                  name="other_ctc"
                                  defaultValue={defaultValues?.other_ctc || ''}
                                  rules={{
                                    validate: (value) => {
                                      const wordCount = value.trim().split(/\s+/).length;
                                      return wordCount <= 25 || "Maximum 25 words allowed.";
                                    }
                                  }}
                                  render={({ field }) => (
                                    <TextField
                                      {...field}
                                      id="other_ctc"
                                      name="other_ctc"
                                      label="Other Perks"
                                      variant="outlined"
                                      size="small"
                                      defaultValue={defaultValues?.other_ctc || ''}
                                      error={!!errors.other_ctc}
                                      helperText={errors.other_ctc?.message}
                                      onChange={(e) => {
                                        const inputValue = e.target.value;
                                        const words = inputValue.trim().split(/\s+/);
                                        if (words.length <= 25) {
                                          field.onChange(inputValue);
                                          clearErrors('other_ctc');
                                        } else {
                                          setError('other_ctc', { message: "Maximum 25 words allowed." });
                                        }
                                        handleSingleChange({ other_ctc: e.target.value });
                                      }}
                                    />
                                  )}
                                />
                              </FormControl>
                            </Grid>
                            <Grid item xs={12} md={6}>
                              {/* total ctc */}
                              <Box className="compensation-det-block">
                                {watch('total_ctc') && watch('total_ctc') > '0' ?
                                  <Box className="after-comp-amt" >
                                    <Typography className="comp-text">Your Total Annual Compensation</Typography>
                                    <Typography className="comp-amt" mt={2}>
                                      ₹{watch('total_ctc') && `${new Intl.NumberFormat('en-IN').format(watch('total_ctc'))}`}
                                    </Typography>
                                    <Typography className="comp-text">{watch('other_ctc') && `Along with ${watch('other_ctc')}`}</Typography>
                                  </Box> :
                                  <Box className="before-comp-amt">
                                    <Typography className="comp-text">Please enter your compensation details on the left</Typography>
                                  </Box>}
                              </Box>
                            </Grid>
                          </Grid>
                        </Box>
                      </Box>
                      <Divider sx={{ borderStyle: 'dashed', borderWidth: '1px', marginBottom: '1rem' }} />
                      <Typography className="step-heading" variant="h6" sx={{ marginBottom: '16px!important' }}>Expected Annual Compensation</Typography>
                      <Grid container spacing={5} mb={3} mt={-3}>
                        <Grid item xs={12} md={6}>
                          {/* expected ctc */}
                          <FormControl fullWidth size="small" error={!!errors?.expected_ctc}>
                            <Controller
                              control={control}
                              name="expected_ctc"
                              defaultValue={defaultValues?.expected_ctc || ''}
                              render={({ field }) => {
                                const [isEditing, setIsEditing] = React.useState(false);

                                return (
                                  <TextField
                                    {...field}
                                    id="expected_ctc"
                                    name="expected_ctc"
                                    label="Expected Annual Compensation (Optional)"
                                    variant="outlined"
                                    size="small"
                                    error={!!errors.expected_ctc}
                                    helperText={errors.expected_ctc?.message}
                                    value={
                                      isEditing
                                        ? field.value
                                        : field.value
                                          ? new Intl.NumberFormat('en-IN').format(field.value)
                                          : ''
                                    }
                                    onFocus={() => setIsEditing(true)}
                                    onBlur={(e) => {
                                      setIsEditing(false);
                                      let rawValue = e.target.value.replace(/,/g, '');
                                      let numValue = parseInt(rawValue, 10);

                                      if (!isNaN(numValue)) {
                                        field.onChange(numValue);
                                        handleSingleChange({ expected_ctc: numValue });
                                      }
                                    }}
                                    onChange={(e) => {
                                      let rawValue = e.target.value.replace(/,/g, '');
                                      if (/^\d{0,9}$/.test(rawValue)) {
                                        field.onChange(rawValue);
                                        handleSingleChange({ expected_ctc: rawValue });
                                      }
                                    }}
                                    inputProps={{
                                      inputMode: 'numeric',
                                      pattern: '[0-9]*',
                                      maxLength: 9
                                    }}
                                    InputProps={{
                                      startAdornment: (
                                        field?.value ? (
                                          <InputAdornment position="start">
                                            <CurrencyRupeeIcon fontSize="small" />
                                          </InputAdornment>) : null
                                      ),
                                    }}
                                  />
                                );
                              }}
                            />
                            <FormHelperText error={false}>Including fixed & variable components</FormHelperText>
                          </FormControl>
                        </Grid>
                      </Grid>
                      <Divider sx={{ borderStyle: 'dashed', borderWidth: '1px', marginBottom: '1rem' }} />

                      <Typography className="step-heading" variant="h6" sx={{ marginBottom: '16px!important' }}>Notice Period</Typography>
                      <Box className="current-job-det" mt={2}>
                        <Grid container spacing={{ xs: 2.5, md: 5 }}>
                          <Grid item xs={12} md={6}>
                            {/* notice period */}
                            <FormControl fullWidth size="small" error={!!errors.notice_period}>
                              <InputLabel id="notice_period">Notice Period</InputLabel>
                              <Controller
                                control={control}
                                name="notice_period"
                                defaultValue={defaultValues?.notice_period || ''}
                                render={({ field }) => (
                                  <Select
                                    {...field}
                                    {...register('notice_period')}
                                    labelId="notice_period"
                                    label="Notice Period"
                                    variant="outlined"
                                    fullWidth
                                    onChange={(e) => {
                                      field.onChange(e.target.value);
                                      handleSingleChange({ notice_period: e.target.value });
                                    }
                                    }
                                  >
                                    <MenuItem value="0 days (immediate)">0 days (immediate)</MenuItem>
                                    <MenuItem value="15">15 Days</MenuItem>
                                    <MenuItem value="30">30 Days</MenuItem>
                                    <MenuItem value="45">45 Days</MenuItem>
                                    <MenuItem value="60">60 Days</MenuItem>
                                    <MenuItem value="75">75 Days</MenuItem>
                                    <MenuItem value="90">90 Days</MenuItem>
                                    <MenuItem value="90+">90+ Days</MenuItem>
                                  </Select>
                                )}
                              />
                              {errors.notice_period && <FormHelperText error>{errors.notice_period?.message}</FormHelperText>}
                            </FormControl>

                            {/* is negotiable */}
                            <FormControl error={!!errors.is_negotiable} className="neogtiable-chk" size="small">
                              <Controller
                                name="is_negotiable"
                                control={control}
                                render={({ field }) => (
                                  <FormControlLabel
                                    control={
                                      <BpCheckbox
                                        {...field}
                                        {...register('is_negotiable')}
                                        id="is_negotiable"
                                        name="is_negotiable"
                                        checked={!!field.value}
                                        disabled={isUpdatingNegotiable}
                                        onChange={async (event) => {
                                          const isChecked = event.target.checked;
                                          field.onChange(isChecked);
                                          setIsUpdatingNegotiable(true);
                                          let dataToUpdate = { is_negotiable: isChecked };
                                          if (!isChecked) {
                                            setValue('buyout_period', '');
                                            dataToUpdate.buyout_period = '';
                                          }
                                          try {
                                            await handleSingleChange(dataToUpdate);
                                            if (isChecked) {
                                              setTimeout(() => {
                                                buyoutFieldRef.current?.focus();
                                              }, 0);
                                            }
                                          } catch (error) {
                                            field.onChange(!isChecked);
                                          } finally {
                                            setIsUpdatingNegotiable(false);
                                          }
                                        }}
                                      />
                                    }
                                    label="Notice period is negotiable"
                                    id="is_negotiable"
                                    sx={{ paddingLeft: 0.5 }}
                                  />
                                )}
                              />
                              {errors.is_negotiable && (
                                <Typography variant="caption" color="error">
                                  {errors.is_negotiable.message}
                                </Typography>
                              )}
                            </FormControl>
                          </Grid>

                          <Grid item xs={12} md={6} mb={1}>
                            {/* buyout period */}
                            <FormControl fullWidth>
                              <Controller
                                control={control}
                                name="buyout_period"
                                defaultValue={defaultValues?.buyout_period || ''}
                                render={({ field }) => (
                                  <TextField
                                    {...field}
                                    inputRef={buyoutFieldRef}
                                    id="buyout_period"
                                    label="If notice period is negotiable, please state how much (Optional)"
                                    name="buyout_period"
                                    disabled={!watch('is_negotiable')}
                                    variant="outlined"
                                    size="small"
                                    onChange={(e) => {
                                      field.onChange(e.target.value);
                                      handleSingleChange({ buyout_period: e.target.value });
                                    }}
                                    error={!!errors.buyout_period}
                                    helperText={errors.buyout_period?.message}
                                  />
                                )}
                              />
                            </FormControl>
                          </Grid>
                        </Grid>
                      </Box>
                    </Box>
                  </Box>
                </Box>}
              {/* part time */}
              {(jobPreference?.includes('Part time') || jobPreference?.includes('both')) &&
                <Box mb={2.5}>
                  <Box className="card-container">
                    <Box className="card-title">
                      <Typography className="title-text">Part Time Job Details</Typography>
                    </Box>
                    <Box className="card-body">
                      <Box>
                        <Grid container spacing={{ xs: 2.5, md: 5 }}>
                          <Grid item xs={12} md={6}>
                            {/* hourly rate */}
                            <FormControl fullWidth error={!!errors?.hourly_rate}>
                              <Controller
                                control={control}
                                name="hourly_rate"
                                defaultValue={defaultValues?.hourly_rate || ''}
                                render={({ field }) => {
                                  const [isEditing, setIsEditing] = React.useState(false);

                                  return (
                                    <TextField
                                      {...field}
                                      id="hourly_rate"
                                      name="hourly_rate"
                                      label="Hourly Rate (per hour)"
                                      variant="outlined"
                                      size="small"
                                      error={!!errors.hourly_rate}
                                      helperText={errors.hourly_rate?.message}
                                      type="text"
                                      value={
                                        isEditing
                                          ? field.value
                                          : field.value
                                            ? new Intl.NumberFormat('en-IN').format(field.value)
                                            : ''
                                      }
                                      onFocus={() => setIsEditing(true)}
                                      onBlur={(e) => {
                                        setIsEditing(false);
                                        let rawValue = e.target.value.replace(/,/g, '');
                                        let numValue = parseInt(rawValue, 10);
                                        if (!isNaN(numValue)) {
                                          field.onChange(numValue);
                                          handleSingleChange({ hourly_rate: numValue });
                                        }
                                      }}
                                      onChange={(e) => {
                                        let rawValue = e.target.value.replace(/,/g, '');
                                        if (/^\d{0,6}$/.test(rawValue)) {
                                          field.onChange(rawValue);
                                          handleSingleChange({ hourly_rate: rawValue });
                                        }
                                      }}
                                      inputProps={{
                                        inputMode: 'numeric',
                                        pattern: '[0-9]*',
                                        maxLength: 6
                                      }}
                                      InputProps={{
                                        startAdornment: field?.value ? (
                                          <InputAdornment position="start">
                                            <CurrencyRupeeIcon fontSize="small" />
                                          </InputAdornment>
                                        ) : null
                                      }}
                                    />
                                  );
                                }}
                              />
                            </FormControl>
                          </Grid>
                          <Grid item xs={12} md={6}>
                            {/* hour */}
                            <FormControl fullWidth>
                              <Controller
                                control={control}
                                name="hour"
                                defaultValue={defaultValues?.hour || ''}
                                render={({ field }) => (
                                  <TextField
                                    {...field}
                                    id="hour"
                                    name="hour"
                                    label="Your Availability (hours per week)"
                                    variant="outlined"
                                    size="small"
                                    value={field.value || ''}
                                    type="text"
                                    error={!!errors.hour}
                                    helperText={errors.hour?.message}
                                    onChange={(e) => {
                                      const inputValue = e.target.value;
                                      if (/^\d{0,2}$/.test(inputValue)) {
                                        field.onChange(inputValue);
                                        handleSingleChange({ hour: inputValue });
                                      }
                                    }}
                                    inputProps={{
                                      inputMode: 'numeric',
                                      pattern: '[0-9]*',
                                      maxLength: 2
                                    }}
                                  />
                                )}
                              />
                            </FormControl>
                          </Grid>
                        </Grid>
                      </Box>
                    </Box>
                  </Box>
                </Box>
              }

              <Divider sx={{ borderStyle: 'dashed', borderWidth: '1px', marginBottom: '1.5rem' }} />

              <Box className="form-action-btn">
                <Stack direction="row" spacing={{ xs: 0, md: 2 }} mt={{ xs: 0, md: 3 }}>
                  <Button
                    variant="contained"
                    className="next-btn"
                    size="large"
                    type="submit"
                    sx={{ backgroundColor: '#1575C6!important', width: { xs: '100%', sm: 'auto' }, }}
                    onClick={onSubmit}
                    disabled={isSubmitting}>
                    {isSubmitting ? 'Saving...' : 'Next'}
                  </Button>
                </Stack>
              </Box>
            </Box>
          </Container>
        </Box>
      </FormProvider>
    </Box>
  );
};

export default JobPreferences;
